import { keyPartDefaults, keyPartRulesDefaults, siteKindDefaults } from '../../../lib/s2/defaults';
import type {
  HaulClassRuleDef,
  KeyPartBeatDef,
  KeyPartDef,
  KeyPartRulesDef,
  SiteKindDef,
  VirtualSiteDef,
} from '../../../lib/s2/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import type { Schemas } from '@argzombie/api-client';
import type { FieldErrors } from '../../../lib/validate';

import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ItemPicker,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
} from '../fields';
import { useContentBundle, useContentRefs, type Option } from '../refs';
import type { FormProps } from '../types';
import { IdListEditor } from './kv';

type Stack = Schemas['ItemStack'];

const idOptions = (xs: { id?: string | null; name?: string | null }[] | undefined): Option[] =>
  (xs ?? []).map((x) => ({
    value: x.id ?? '',
    label: x.name && x.name !== x.id ? `${x.name} (${x.id})` : (x.id ?? ''),
  }));

/** A kind of site a key part can be found at (key-parts.html): matched by OSM tags to a building category. */
export function SiteKindForm({ value, onChange, errors }: FormProps<SiteKindDef>) {
  const s = withDefaults(siteKindDefaults(), value ?? {}) as Required<SiteKindDef>;
  const set = <K extends keyof SiteKindDef>(k: K, v: SiteKindDef[K]) => onChange({ ...s, [k]: v });
  const { categories } = useContentRefs();
  return (
    <Fieldset legend="Site kind">
      <TextField label="Id" mono value={s.id} issues={errors.id} onChange={(v) => set('id', v)} />
      <TextField
        label="Name"
        value={s.name}
        issues={errors.name}
        onChange={(v) => set('name', v)}
      />
      <StringListEditor
        label="OSM tags"
        base="osmTags"
        hint="e.g. shop=agrarian, amenity=restaurant+cuisine=pizza"
        value={s.osmTags}
        errors={errors}
        onChange={(v) => set('osmTags', v)}
      />
      <SelectField
        label="Category"
        value={s.category}
        options={idOptions(categories)}
        issues={errors.category}
        onChange={(v) => set('category', v ?? '')}
      />
      <CheckboxField
        label="In person allowed"
        checked={s.inPersonAllowed}
        onChange={(v) => set('inPersonAllowed', v)}
      />
    </Fieldset>
  );
}

/** A key part (key-parts.html): the tier-gating pieces, where to find them and what happens there. */
export function KeyPartForm({ value, onChange, errors }: FormProps<KeyPartDef>) {
  const k = withDefaults(keyPartDefaults(), value ?? {}) as Required<KeyPartDef>;
  const set = <K extends keyof KeyPartDef>(key: K, v: KeyPartDef[K]) =>
    onChange({ ...k, [key]: v });
  const b = useContentBundle();
  const { items, categories } = useContentRefs();
  const hasKeyPartItems = items.some((i) => i.category === 'key_part');
  const siteKinds = idOptions(b.siteKinds);
  const virtualSites = idOptions(b.keyPartRules?.virtualSites);
  const beatChains = (b.beatChains ?? []).map((c) => ({
    value: c.id ?? '',
    label: c.title && c.title !== c.id ? `${c.title} (${c.id})` : (c.id ?? ''),
  }));

  return (
    <>
      <Fieldset legend="Key part">
        <TextField label="Id" mono value={k.id} issues={errors.id} onChange={(v) => set('id', v)} />
        <NumberField
          label="Tier"
          integer
          min={0}
          value={k.tier}
          issues={errors.tier}
          onChange={(v) => set('tier', v ?? 0)}
        />
        <TextField
          label="Name"
          value={k.name}
          issues={errors.name}
          onChange={(v) => set('name', v)}
        />
        <TextField
          label="Mission title"
          value={k.missionTitle}
          issues={errors.missionTitle}
          onChange={(v) => set('missionTitle', v)}
        />
        <TextField
          label="Hint"
          multiline
          value={k.hint}
          issues={errors.hint}
          onChange={(v) => set('hint', v)}
        />
        <PiecesEditor
          value={k.pieces}
          errors={errors}
          onChange={(v) => set('pieces', v)}
          restrict={hasKeyPartItems}
        />
      </Fieldset>
      <Fieldset legend="Where to find it">
        <IdListEditor
          label="Site kinds"
          what="site kind"
          base="siteKinds"
          hint="Best first."
          options={siteKinds}
          value={k.siteKinds}
          errors={errors}
          onChange={(v) => set('siteKinds', v)}
        />
        <IdListEditor
          label="Fallback categories"
          what="category"
          base="fallbackCategories"
          options={idOptions(categories)}
          value={k.fallbackCategories}
          errors={errors}
          onChange={(v) => set('fallbackCategories', v)}
        />
        <IdListEditor
          label="Virtual sites"
          what="virtual site"
          base="virtualSiteIds"
          options={virtualSites}
          value={k.virtualSiteIds}
          errors={errors}
          onChange={(v) => set('virtualSiteIds', v)}
        />
        <SelectField
          label="Scenario"
          none="(none)"
          value={k.scenarioId}
          options={idOptions(b.scenarios)}
          issues={errors.scenarioId}
          onChange={(v) => set('scenarioId', v ?? '')}
        />
        <SelectField
          label="Set piece"
          none="(none)"
          value={k.setPieceId}
          options={idOptions(b.setPieces)}
          issues={errors.setPieceId}
          onChange={(v) => set('setPieceId', v ?? '')}
        />
      </Fieldset>
      <Fieldset legend="Beats">
        <ListEditor<KeyPartBeatDef>
          label="Beats"
          what="beat"
          items={k.beats}
          issues={errors.beats}
          onChange={(v) => set('beats', v)}
          make={() => ({ beatId: '', chance: 1 })}
          row={(beat, j, setBeat) => (
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Beat chain" issues={errors[`beats.${j}.beatId`]}>
                {(id, invalid) => (
                  <SelectInput
                    id={id}
                    invalid={invalid}
                    value={beat.beatId}
                    options={beatChains}
                    onChange={(v) => setBeat({ ...beat, beatId: v ?? '' })}
                  />
                )}
              </Field>
              <NumberField
                label="Chance"
                hint="0..1"
                min={0}
                max={1}
                step={0.05}
                value={beat.chance}
                issues={errors[`beats.${j}.chance`]}
                onChange={(v) => setBeat({ ...beat, chance: v ?? 0 })}
              />
            </div>
          )}
        />
      </Fieldset>
    </>
  );
}

/**
 * The pieces list. Pieces are `key_part` items; the picker offers only those when the draft has any, else every item (so a
 * designer can still pick something before the category exists).
 */
function PiecesEditor({
  value,
  errors,
  onChange,
  restrict,
}: {
  value: KeyPartDef['pieces'];
  errors: FieldErrors;
  onChange: (next: Stack[]) => void;
  restrict: boolean;
}) {
  return (
    <ListEditor<Stack>
      label="Pieces"
      what="piece"
      hint={
        restrict
          ? 'All pieces are needed to finish the key part. Only key_part items are offered.'
          : 'No key_part items yet: any item can be a piece.'
      }
      items={value ?? []}
      issues={errors.pieces}
      onChange={onChange}
      make={() => ({ itemId: '', quantity: 1 })}
      row={(p, j, set) => (
        <div className="grid grid-cols-[minmax(0,1fr)_6rem] gap-2">
          <div>
            <ItemPicker
              label={`Pieces ${j + 1} item`}
              filter={restrict ? (c) => c === 'key_part' : undefined}
              value={p.itemId}
              invalid={!!errors[`pieces.${j}.itemId`]}
              onChange={(v) => set({ ...p, itemId: v ?? '' })}
            />
            <IssueList issues={errors[`pieces.${j}.itemId`]} />
          </div>
          <div>
            <NumberInput
              label={`Pieces ${j + 1} quantity`}
              integer
              min={1}
              value={p.quantity}
              onChange={(v) => set({ ...p, quantity: v ?? 0 })}
            />
            <IssueList issues={errors[`pieces.${j}.quantity`]} />
          </div>
        </div>
      )}
    />
  );
}

type HaulKey = 'heavy' | 'twoPerson' | 'drag';

/** The key part rules section (key-parts.html): site timing, the haul classes, and the virtual sites. */
export function KeyPartRulesForm({ value, onChange, errors }: FormProps<KeyPartRulesDef>) {
  const d = keyPartRulesDefaults();
  const base = withDefaults(d, value ?? {}) as Required<KeyPartRulesDef>;
  const r: Required<KeyPartRulesDef> = {
    ...base,
    haul: {
      heavy: { ...d.haul.heavy, ...base.haul?.heavy },
      twoPerson: { ...d.haul.twoPerson, ...base.haul?.twoPerson },
      drag: { ...d.haul.drag, ...base.haul?.drag },
    } as Required<KeyPartRulesDef['haul']> & object,
    virtualSites: base.virtualSites ?? [],
  };
  const set = <K extends keyof KeyPartRulesDef>(key: K, v: KeyPartRulesDef[K]) =>
    onChange({ ...r, [key]: v });
  const { categories } = useContentRefs();
  const b = useContentBundle();
  const num = (
    k: keyof KeyPartRulesDef & string,
    label: string,
    o: { integer?: boolean; hint?: string } = {},
  ) => (
    <NumberField
      key={k}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={0}
      value={r[k] as number}
      issues={errors[k]}
      onChange={(v) => set(k, (v ?? 0) as never)}
    />
  );
  const haul = r.haul as Record<HaulKey, Required<HaulClassRuleDef>>;
  const setHaul = (cls: HaulKey, f: keyof HaulClassRuleDef, v: number) =>
    set('haul', { ...haul, [cls]: { ...haul[cls], [f]: v } });
  const HAUL: [HaulKey, string][] = [
    ['heavy', 'Heavy'],
    ['twoPerson', 'Two-person'],
    ['drag', 'Drag'],
  ];

  return (
    <>
      <Fieldset legend="Sites and pieces">
        {num('hardLimitPct', 'Hard limit (%)', {
          integer: true,
          hint: 'Carry weight over this share of capacity stops the walk.',
        })}
        {num('farRadiusM', 'Far radius (m)')}
        {num('siteTtlHours', 'Site lifetime (hours)', { integer: true })}
        {num('rerollHours', 'Re-roll (hours)', { integer: true })}
        {num('lostPieceRespawnHours', 'Lost piece respawn (hours)', { integer: true })}
      </Fieldset>
      {HAUL.map(([cls, title]) => (
        <Fieldset key={cls} legend={`Haul: ${title}`}>
          <NumberField
            label={`${title} speed`}
            hint="Walk speed multiplier."
            min={0}
            value={haul[cls].speed}
            issues={errors[`haul.${cls}.speed`]}
            onChange={(v) => setHaul(cls, 'speed', v ?? 0)}
          />
          <NumberField
            label={`${title} pickup (s)`}
            min={0}
            value={haul[cls].pickupS}
            issues={errors[`haul.${cls}.pickupS`]}
            onChange={(v) => setHaul(cls, 'pickupS', v ?? 0)}
          />
          <NumberField
            label={`${title} noise (m)`}
            min={0}
            value={haul[cls].noiseM}
            issues={errors[`haul.${cls}.noiseM`]}
            onChange={(v) => setHaul(cls, 'noiseM', v ?? 0)}
          />
          <NumberField
            label={`${title} noise every (s)`}
            min={0}
            value={haul[cls].everyS}
            issues={errors[`haul.${cls}.everyS`]}
            onChange={(v) => setHaul(cls, 'everyS', v ?? 0)}
          />
        </Fieldset>
      ))}
      <Fieldset legend="Virtual sites">
        <ListEditor<VirtualSiteDef>
          label="Virtual sites"
          what="site"
          items={r.virtualSites}
          issues={errors.virtualSites}
          onChange={(v) => set('virtualSites', v)}
          make={() => ({ id: '', name: '', line: '', category: '', roomCount: 4, setPieceId: '' })}
          row={(s, j, setS) => (
            <div className="grid gap-2 sm:grid-cols-2">
              <TextField
                label="Id"
                mono
                value={s.id}
                issues={errors[`virtualSites.${j}.id`]}
                onChange={(v) => setS({ ...s, id: v })}
              />
              <TextField
                label="Name"
                value={s.name}
                issues={errors[`virtualSites.${j}.name`]}
                onChange={(v) => setS({ ...s, name: v })}
              />
              <TextField
                label="Line"
                multiline
                value={s.line}
                issues={errors[`virtualSites.${j}.line`]}
                onChange={(v) => setS({ ...s, line: v })}
              />
              <SelectField
                label="Category"
                value={s.category}
                options={idOptions(categories)}
                issues={errors[`virtualSites.${j}.category`]}
                onChange={(v) => setS({ ...s, category: v ?? '' })}
              />
              <Field label="Room count" issues={errors[`virtualSites.${j}.roomCount`]}>
                {(id, invalid) => (
                  <NumberInput
                    id={id}
                    invalid={invalid}
                    integer
                    min={1}
                    value={s.roomCount}
                    onChange={(v) => setS({ ...s, roomCount: v ?? 0 })}
                  />
                )}
              </Field>
              <SelectField
                label="Set piece"
                none="(none)"
                value={s.setPieceId}
                options={idOptions(b.setPieces)}
                issues={errors[`virtualSites.${j}.setPieceId`]}
                onChange={(v) => setS({ ...s, setPieceId: v ?? '' })}
              />
            </div>
          )}
        />
      </Fieldset>
    </>
  );
}
